import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Participant } from '../types';

interface ParticipantsListProps {
  participants: Participant[];
  currentUserRole: 'admin' | 'teacher' | 'student' | 'parent';
  onToggleAudio: (participantId: string) => void;
  onLowerHand: (participantId: string) => void;
  onRemoveParticipant?: (participantId: string) => void;
  isOpen: boolean;
  onClose: () => void;
}

const ParticipantsList = ({
  participants,
  currentUserRole,
  onToggleAudio,
  onLowerHand,
  onRemoveParticipant,
  isOpen,
  onClose,
}: ParticipantsListProps) => {
  const [searchQuery, setSearchQuery] = useState('');

  const filteredParticipants = participants.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const canModerate = currentUserRole === 'teacher' || currentUserRole === 'admin';
  const teachers = filteredParticipants.filter((p) => p.role === 'teacher' || p.role === 'admin');
  const students = filteredParticipants.filter((p) => p.role === 'student');
  const parents = filteredParticipants.filter((p) => p.role === 'parent');

  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-16 bottom-0 w-80 bg-card border-l border-border shadow-lg z-[950] flex flex-col animate-slide-in-right lg:w-96">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <div className="flex items-center gap-2">
          <Icon name="Users" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">
            Participanți ({participants.length})
          </h3>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-md hover:bg-muted transition-colors duration-150"
          aria-label="Închide panoul cu participanți"
        >
          <Icon name="X" size={20} className="text-muted-foreground" />
        </button>
      </div>

      <div className="p-4 border-b border-border">
        <div className="relative">
          <Icon
            name="Search"
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="text"
            placeholder="Caută participanți..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 bg-muted border border-border rounded-md text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {teachers.length > 0 && (
          <div className="p-4 border-b border-border">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
              Profesori ({teachers.length})
            </h4>
            <div className="space-y-2">
              {teachers.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors duration-150"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold">
                        {participant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {participant.name}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5">
                        {participant.isPresenting && (
                          <span className="text-xs text-error font-medium">
                            Prezintă
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Icon
                      name={participant.isAudioOn ? 'Mic' : 'MicOff'}
                      size={16}
                      className={
                        participant.isAudioOn
                          ? 'text-success' :'text-muted-foreground'
                      }
                    />
                    <Icon
                      name={participant.isVideoOn ? 'Video' : 'VideoOff'}
                      size={16}
                      className={
                        participant.isVideoOn
                          ? 'text-success' :'text-muted-foreground'
                      }
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {students.length > 0 && (
          <div className="p-4">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
              Elevi ({students.length})
            </h4>
            <div className="space-y-2">
              {students.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors duration-150"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold">
                        {participant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {participant.name}
                      </p>
                      {participant.isHandRaised && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Icon name="Hand" size={12} className="text-warning" />
                          <span className="text-xs text-warning font-medium">
                            Mână ridicată
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon
                      name={participant.isAudioOn ? 'Mic' : 'MicOff'}
                      size={16}
                      className={
                        participant.isAudioOn
                          ? 'text-success' :'text-muted-foreground'
                      }
                    />
                    <Icon
                      name={participant.isVideoOn ? 'Video' : 'VideoOff'}
                      size={16}
                      className={
                        participant.isVideoOn
                          ? 'text-success' :'text-muted-foreground'
                      }
                    />
                    {canModerate && (
                      <div className="flex items-center gap-1 ml-2">
                        {participant.isHandRaised && (
                          <button
                            onClick={() => onLowerHand(participant.id)}
                            className="p-1 rounded hover:bg-muted-foreground/10 transition-colors duration-150"
                            aria-label="Coboară mâna"
                          >
                            <Icon
                              name="HandMetal"
                              size={14}
                              className="text-warning"
                            />
                          </button>
                        )}
                        {!participant.isAudioOn && (
                          <button
                            onClick={() => onToggleAudio(participant.id)}
                            className="p-1 rounded hover:bg-muted-foreground/10 transition-colors duration-150"
                            aria-label="Activează microfonul participantului"
                          >
                            <Icon
                              name="Mic"
                              size={14}
                              className="text-muted-foreground"
                            />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {parents.length > 0 && (
          <div className="p-4 border-t border-border">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-3">
              Părinți ({parents.length})
            </h4>
            <div className="space-y-2">
              {parents.map((participant) => (
                <div
                  key={participant.id}
                  className="flex items-center justify-between p-2 rounded-md hover:bg-muted transition-colors duration-150"
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-full bg-secondary text-secondary-foreground flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold">
                        {participant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground truncate">
                        {participant.name}
                      </p>
                      {participant.isHandRaised && (
                        <div className="flex items-center gap-1 mt-0.5">
                          <Icon name="Hand" size={12} className="text-warning" />
                          <span className="text-xs text-warning font-medium">
                            Mână ridicată
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon
                      name={participant.isAudioOn ? 'Mic' : 'MicOff'}
                      size={16}
                      className={participant.isAudioOn ? 'text-success' : 'text-muted-foreground'}
                    />
                    <Icon
                      name={participant.isVideoOn ? 'Video' : 'VideoOff'}
                      size={16}
                      className={participant.isVideoOn ? 'text-success' : 'text-muted-foreground'}
                    />
                    {canModerate && (
                      <div className="flex items-center gap-1 ml-2">
                        {participant.isHandRaised && (
                          <button
                            onClick={() => onLowerHand(participant.id)}
                            className="p-1 rounded hover:bg-muted-foreground/10 transition-colors duration-150"
                            aria-label="Coboară mâna"
                          >
                            <Icon name="HandMetal" size={14} className="text-warning" />
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {canModerate && (
        <div className="p-4 border-t border-border bg-muted/30">
          <Button
            variant="outline"
            size="sm"
            fullWidth
            iconName="UserPlus"
            iconPosition="left"
          >
            Invită participanți
          </Button>
        </div>
      )}
    </div>
  );
};

export default ParticipantsList;
